import { getAuth, onAuthStateChanged, onIdTokenChanged } from "firebase/auth";
import { useFirebaseApp } from 'vuefire';
import { useUserStore } from '~/stores/user';

async function getCurrentUser() {
  return new Promise((resolve, reject) => {
    const auth = getAuth(useFirebaseApp());
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    }, reject);
  });
}

export default defineNuxtRouteMiddleware(async (to, from) => {
  if (process.server) {
    return;
  }

  const auth = getAuth(useFirebaseApp());
  const userStore = useUserStore();
  const { $helpers } = useNuxtApp();

  // Check if route requires authentication
  const protectedRoutes = ['/profile', '/bookmarks'];
  const requiresAuth = protectedRoutes.some(route => to.path.startsWith(route));

  if (requiresAuth) {
    const user = await getCurrentUser();
    
    if (!user) {
      return navigateTo({
        path: '/',
        query: {
          redirect: to.fullPath,
        },
      });
    }

    // Set user data immediately for protected routes
    try {
      const token = await user.getIdToken();
      const decodedToken = $helpers.decodeToken(token);
      
      userStore.setUser({
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        userId: decodedToken.user?.id,
        verification: decodedToken.verification || null
      });
    } catch (error) {
      console.error('Error setting user data:', error);
      return navigateTo('/');
    }
  }

  // Set up listeners for auth state changes (non-blocking)
  onAuthStateChanged(auth, (user) => {
    if (user) {
      user.getIdToken().then(token => {
        let decodedToken = $helpers.decodeToken(token);
        userStore.setUser({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          userId: decodedToken.user?.id,
          verification: decodedToken.verification || null
        });
      });
    } else {
      userStore.clearUser();
    }
  });

  // Listen for token changes to update specific fields
  onIdTokenChanged(auth, async (user) => {
    if (user && userStore.user) {
      try {
        const token = await user.getIdToken();
        const decodedToken = $helpers.decodeToken(token);
        
        // Preserve 'verified' status if it was already set
        let verificationStatus = decodedToken.verification || null;
        if (userStore.user.verification === 'verified' && !decodedToken.verification) {
          verificationStatus = 'verified';
        }
        
        // Only update specific fields when token changes
        userStore.setUser({
          ...userStore.user,
          displayName: user.displayName,
          photoURL: user.photoURL,
          verification: verificationStatus
        });
      } catch (error) {
        console.error('Error updating user from token change:', error);
      }
    }
  });
});